import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Page, Card, Text, BlockStack, Button, ButtonGroup, InlineStack, Badge } from '../components/admin-ui';
import { settingsRequest, settingsError, revokePaymentOnExit } from '../api/adminSettings';
import { SecretField, SettingsFeedback, SettingsLoading } from '../components/SettingsFields';
import PaymentAccounts from '../components/PaymentAccounts';
import CardPaymentRoute from '../componens/cardPaymentRoute';

function newVisit() {
  return Array.from(window.crypto.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, '0')).join('');
}
export default function PaymentIntegration() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const visit = useRef(null);
  if (!visit.current) visit.current = newVisit();
  const token = useRef('');
  const active = useRef(true);
  const request = useCallback(async (endpoint, params = {}) => {
    try {
      return await settingsRequest(endpoint, { ...params, payment_visit_id: visit.current, payment_access_token: token.current });
    } catch (e) {
      if (e.response?.status === 403 && active.current) {
        token.current = ''; setUnlocked(false); setData(null); setStatus({ password_set: true });
      }
      throw e;
    }
  }, []);
  const loadData = useCallback(async () => {
    const result = await request('g-payment-settings');
    if (active.current) setData(result);
  }, [request]);
  const load = useCallback(async () => {
    setBusy(true); setError('');
    try {
      const result = await settingsRequest('g-payment-password');
      if (typeof result?.password_set !== 'boolean') throw new Error('Unable to read password status.');
      if (!active.current) return;
      setStatus(result);
      if (!result.password_set) { setUnlocked(true); await loadData(); }
    } catch (e) { if (active.current) setError(settingsError(e)); }
    finally { if (active.current) setBusy(false); }
  }, [loadData]);
  useEffect(() => {
    active.current = true; load();
    const revoke = () => {
      if (token.current) settingsRequest('revoke-payment-access', { payment_access_token: token.current }).catch(() => {});
      token.current = '';
    };
    const onHide = () => { if (token.current) revokePaymentOnExit(token.current); token.current = ''; setUnlocked(false); setData(null); };
    const onShow = event => { if (event.persisted) load(); };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('pageshow', onShow);
    return () => { active.current = false; revoke(); window.removeEventListener('pagehide', onHide); window.removeEventListener('pageshow', onShow); };
  }, [load]);
  const verify = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const result = await settingsRequest('verify-payment-password', { password, payment_visit_id: visit.current });
      if (!active.current) { settingsRequest('revoke-payment-access', result).catch(() => {}); return; }
      token.current = result.payment_access_token;
      setPassword(''); setUnlocked(true); await loadData();
    } catch (e) { if (active.current) setError(settingsError(e)); }
    finally { if (active.current) setBusy(false); }
  };
  const environment = async isTest => {
    setBusy(true); setError('');
    try { const result = await request('u-modal', { modal: isTest ? 1 : 0 }); setData(prev => ({ ...prev, is_test: Boolean(Number(result.modal)) })); }
    catch (e) { setError(settingsError(e)); }
    finally { setBusy(false); }
  };
  return <Page><div className="settings-stack">
    <SettingsFeedback error={error} retry={!status ? load : unlocked && !data ? async () => { setError(''); try { await loadData(); } catch(e) { setError(settingsError(e)); } } : undefined} />
    {!status ? (!error && <SettingsLoading />) : !unlocked ? <div className="settings-password"><Card><form onSubmit={verify}><BlockStack gap="400">
      <Text as="h2" variant="headingLg">Enter secondary password</Text>
      <Text as="p" tone="subdued">Enter your password each time you open Payment integration.</Text>
      <SecretField label="Secondary password" autoComplete="current-password" value={password} onChange={setPassword} disabled={busy} />
      <InlineStack align="end" gap="200"><Button onClick={() => navigate('/index' + location.search)}>Cancel</Button><Button submit variant="primary" loading={busy} disabled={!password}>Continue</Button></InlineStack>
    </BlockStack></form></Card></div> : !data ? <SettingsLoading /> : <>
      <InlineStack align="space-between" blockAlign="center" gap="400">
        <Text as="p" tone="subdued">Manage payment accounts and card routing.</Text>
        <InlineStack gap="300" blockAlign="center"><ButtonGroup variant="segmented">
          <Button pressed={!data.is_test} disabled={busy} onClick={() => environment(false)}>Live</Button>
          <Button pressed={data.is_test} disabled={busy} onClick={() => environment(true)}>Test</Button>
        </ButtonGroup><Badge>{status.password_set ? 'Password protection on' : 'Password protection off'}</Badge></InlineStack>
      </InlineStack>
      <PaymentAccounts accounts={data.accounts} request={request} disabled={busy} onChange={accounts => setData(prev => ({ ...prev, accounts }))} isTest={data.is_test} />
      <CardPaymentRoute key={String(data.is_test)} isTest={data.is_test} initialRoutes={data.routes.filter(route => Boolean(Number(route.is_test)) === data.is_test)} request={request}
        onRoutesChange={routes => setData(prev => ({ ...prev, routes: [...prev.routes.filter(route => Boolean(Number(route.is_test)) !== prev.is_test), ...routes.map(route => ({ ...route, is_test: prev.is_test ? 1 : 0 }))] }))} />
      <Link to={'/administration' + location.search}>Password protection settings</Link>
    </>}
  </div></Page>;
}
